import { getCountryNameFromCountryISOCode } from '@utils';

/**
 * Returns per-rail changes between the original rails map and the form state.
 * Each entry: { rail, from, to } where from/to are booleans for isEnabled.
 */
export const diffRails = (originalRails, formRails) => {
  if (!originalRails || !formRails) return [];

  return Object.entries(originalRails).reduce((changes, [rail, cfg]) => {
    const originalEnabled = cfg.isEnabled ?? false;
    const formEnabled = formRails[rail]?.isEnabled ?? originalEnabled;
    if (originalEnabled !== formEnabled) {
      changes.push({ rail, from: originalEnabled, to: formEnabled });
    }
    return changes;
  }, []);
};

const fastTransferConfigToEntity = (countryCode, countryConfig) => {
  return {
    data: [
      {
        title: 'Country',
        value: countryCode,
        displayValue: `${countryCode} - ${getCountryNameFromCountryISOCode(countryCode)}`,
        showEditIcon: true,
        position: {
          value: 'sticky',
          style: {
            position: 'sticky',
            zIndex: '1',
            left: '0',
            backgroundColor: '#111111',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)',
          },
        },
        name: 'countryCode',
        isCreatable: true,
        isEditable: false,
      },
      {
        name: 'rails',
        title: 'Rails',
        type: 'railList',
        value: countryConfig?.enabledRails || {},
        isEditable: true,
        isCreatable: true,
      },
      {
        title: 'Rail Priorities',
        value: countryConfig?.railPriorities || [],
        maskValue: countryConfig?.railPriorities
          ? countryConfig.railPriorities.join(' > ')
          : '-',
        type: 'textarea',
        inputType: 'string',
        isCreatable: false,
        isEditable: false,
        name: 'railPriorities',
      },
    ],
    type: 'object',
    id: countryCode,
  };
};

/**
 * Convert countries object to entity format
 */
export const fastTransferConfigsToEntity = (countriesData) => {
  if (!countriesData || typeof countriesData !== 'object') {
    return [];
  }

  return Object.keys(countriesData).map((countryCode) =>
    fastTransferConfigToEntity(countryCode, countriesData[countryCode])
  );
};

/**
 * Convert entity back to form data for editing.
 * Captures per-rail isEnabled so each rail toggle is independently editable.
 */
export const entityToFormData = (entity) => {
  if (!entity?.data) return {};

  const railsField = entity.data.find((d) => d.name === 'rails');
  if (!railsField?.value) return { countryCode: entity.id, enabledRails: {} };

  const enabledRails = Object.fromEntries(
    Object.entries(railsField.value).map(([rail, cfg]) => [
      rail,
      { isEnabled: cfg.isEnabled ?? false },
    ])
  );

  return { countryCode: entity.id, enabledRails };
};

/**
 * Convert form data to API payload.
 * Merges per-rail isEnabled from the form into the original country config,
 * preserving activeVersion, railPriorities, and any unknown future keys.
 */
export const formDataToPayload = (formData, countryCode, allCountries) => {
  const originalCountryConfig = allCountries?.[countryCode];

  return {
    countries: {
      ...allCountries,
      [countryCode]: {
        ...originalCountryConfig,
        enabledRails: Object.fromEntries(
          Object.entries(originalCountryConfig?.enabledRails || {}).map(
            ([rail, cfg]) => [
              rail,
              {
                ...cfg,
                isEnabled:
                  formData.enabledRails?.[rail]?.isEnabled ?? cfg.isEnabled,
              },
            ]
          )
        ),
      },
    },
  };
};

/**
 * Get filters configuration
 */
export const getFastTransferFilters = () => {
  return [
    {
      key: 'countryCode',
      text: 'Filter By Country',
      title: 'Country',
      filters: [],
    },
  ];
};

/**
 * Fast Transfer Manager Configuration
 */
export const fastTransferConfig = {
  title: 'Fast Transfer Manager',
  entityConvertor: (data) => fastTransferConfigsToEntity(data),
  entityToFormData: (entity) => entityToFormData(entity),
  payloadConvertor: (formData, countryCode, allCountries) =>
    formDataToPayload(formData, countryCode, allCountries),
  filters: getFastTransferFilters(),
};
